import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accessApi, type PageRegistryEntry } from '@/api/access-api';
import { DataTable, type Column } from '@/acc-ui/primitives/DataTable';
import { Badge } from '@/acc-ui/primitives/Badge';
import { SearchInput } from '@/acc-ui/primitives/SearchInput';
import { HighlightText } from '@/acc-ui/primitives/HighlightText';
import { Button } from '@/acc-ui/primitives/Button';
import { ErrorState } from '@/acc-ui/primitives/Layout';

/**
 * Page Registry tab.
 *
 * The frontend POSTs its PAGE_REGISTRY to the backend on every authed
 * mount. This table shows what the backend currently has. `Inactive`
 * marks pages that the current frontend deploy no longer ships — useful
 * when auditing why a previously-granted permission has no effect.
 */

export function AccessPageRegistryTab() {
  const [search, setSearch] = useState('');
  const query = useQuery({ queryKey: ['pages'], queryFn: accessApi.listPages });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = query.data ?? [];
    if (!q) return all;
    return all.filter(
      (p) =>
        p.path.toLowerCase().includes(q) ||
        p.title.toLowerCase().includes(q) ||
        p.module.toLowerCase().includes(q) ||
        p.requiredPermission.toLowerCase().includes(q),
    );
  }, [query.data, search]);

  const columns: Column<PageRegistryEntry>[] = [
    {
      key: 'title',
      header: 'Page',
      cell: (p) => (
        <div className="flex flex-col">
          <span className="font-semibold text-text-primary">
            <HighlightText text={p.title} query={search} />
          </span>
          <code className="font-mono text-micro text-text-tertiary">
            <HighlightText text={p.path} query={search} />
          </code>
        </div>
      ),
    },
    {
      key: 'module',
      header: 'Module',
      cell: (p) => <Badge tone="muted">{p.module}</Badge>,
      width: '10rem',
    },
    {
      key: 'requiredPermission',
      header: 'Required permission',
      cell: (p) => (
        <code className="font-mono text-caption text-brand-blue bg-brand-blue/5 rounded px-1.5 py-0.5">
          <HighlightText text={p.requiredPermission} query={search} />
        </code>
      ),
    },
    {
      key: 'lastSeenAt',
      header: 'Last seen',
      cell: (p) => (
        <span className="text-caption text-text-tertiary tabular-nums">
          {new Date(p.lastSeenAt).toLocaleString()}
        </span>
      ),
      width: '12rem',
    },
    {
      key: 'isActive',
      header: 'Status',
      cell: (p) =>
        p.isActive ? (
          <Badge tone="success" dot>Active</Badge>
        ) : (
          <Badge tone="neutral" dot>Inactive</Badge>
        ),
      width: '7rem',
    },
  ];

  if (query.isError) {
    return (
      <ErrorState
        description="Could not load the page registry."
        action={<Button onClick={() => query.refetch()}>Retry</Button>}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="flex-1 max-w-md">
          <SearchInput
            placeholder="Search by path, title, or permission…"
            value={search}
            onChange={setSearch}
          />
        </div>
      </div>
      <DataTable<PageRegistryEntry>
        columns={columns}
        rows={filtered}
        rowKey={(p) => p.path}
        page={1}
        pageSize={filtered.length || 25}
        total={filtered.length}
        onPageChange={() => undefined}
        loading={query.isLoading}
      />
    </div>
  );
}
